import { useEffect } from 'react'

import { calculateAge } from '../../../../functions'

export default function useVouchers({
  voucherData,
  formData,
  formType,
  organisationsInfo,
  validateVoucher,
  initVoucherSession,
}) {
  useEffect(() => {
    const { organisation } = formData
    if (Object.keys(organisation.voucherConfig).length) {
      initVoucherSession()
    }
  }, [])

  const appliedVouchers = getAppliedVouchers(voucherData)
  const appliedVoucherCodes = Object.values(appliedVouchers).map(v => v.appliedVoucher)
  const hasAppliedVoucher = appliedVoucherCodes.length > 0

  const applyVoucher = (voucher, callback) => {
    const validationPayload = getValidationPayload(voucher, formData)

    validateVoucher(validationPayload, callback)
  }

  const getShowVouchers = registrations => {
    const { members = [] } = registrations

    return members.reduce((acc, member) => {
      acc[member.id] = getVoucherVisibility(member, formData, formType, organisationsInfo)
      return acc
    }, {})
  }

  return {
    appliedVouchers,
    appliedVoucherCodes,
    hasAppliedVoucher,
    applyVoucher,
    getShowVouchers,
  }
}

export function getAppliedVouchers(voucherData) {
  const appliedVouchers = Object.keys(voucherData).reduce((acc, key) => {
    if (key === 'sessionId') {
      return acc
    }

    const memberVoucherData = voucherData[key]
    const { appliedVoucher } = memberVoucherData || {}

    if (appliedVoucher) {
      acc[key] = memberVoucherData
    }

    return acc
  }, {})

  return appliedVouchers
}

export function getUserPins(formData) {
  const { registrations = {} } = formData || {}
  const userPins = Object.values(registrations).reduce((acc, r) => {
    if (r.birthdate) {
      acc[r.sfid] = r.birthdate.split('-').slice(1).reverse().join('') || ''
    }
    return acc
  }, {})

  return userPins
}

export function getVoucherConfigs(formData) {
  const { organisation = {} } = formData || {}
  return organisation.voucherConfig || []
}

export function getValidationPayload(voucher, formData) {
  const userPins = getUserPins(formData)
  const voucherConfigs = getVoucherConfigs(formData)

  const { memberId, selectedProvider, voucherCode } = voucher

  if (!memberId || !selectedProvider || !voucherCode) {
    return null
  }

  const voucherConfig = voucherConfigs.find(c => c.id === selectedProvider.id)

  if (!voucherConfig) {
    return null
  }

  return {
    memberId,
    pin: userPins[memberId],
    voucherCode,
    voucherType: voucherConfig.voucherType,
    voucherId: voucherConfig.id,
    voucherIssuer: voucherConfig.voucherIssuer,
    provider: selectedProvider,
  }
}

export function getVoucherVisibility(member, formData, formType, organisationsInfo) {
  const isProductOwnerSupportVoucher = product => {
    const org = organisationsInfo.find(org => org.sfid === product.orgId)
    // biome-ignore lint/complexity/useOptionalChain: TODO
    if (org && org.isVoucherEnabled) return true
    return false
  }

  const checkFormType = (config = {}) => {
    const { displayRules = {} } = config
    const { formType: formTypeRule = 'membership' } = displayRules

    if (formTypeRule.toLowerCase() === 'all') {
      return true
    }

    const type = formType.toLowerCase() === 'shop' ? 'membership' : formType.toLowerCase()
    return type === formTypeRule.toLowerCase()
  }

  const checkMemberTypes = (config = {}) => {
    const { displayRules = {} } = config
    const { registeringMemberType = [] } = displayRules
    const { memberTypes = [] } = member || {}

    if (registeringMemberType.length > 0 && memberTypes.length > 0) {
      return registeringMemberType
        .reduce((acc, type) => {
          memberTypes.forEach(mt => acc.push(!!mt[type]))
          return acc
        }, [])
        .every(t => !!t)
    }

    return true
  }

  const checkMemberGrants = (config = {}) => {
    const { displayRules = {} } = config
    const { grantMemberType = [] } = displayRules
    const { products = [] } = member || {}

    if (grantMemberType.length > 0 && products.length > 0) {
      const memberGrants = products.reduce((acc, p) => {
        acc.push(...(p.grantMemberTypes || []))
        return acc
      }, [])

      return grantMemberType
        .reduce((acc, type) => {
          memberGrants.forEach(mg => {
            acc.push(!!mg[type])
          })
          return acc
        }, [])
        .some(t => !!t)
    }

    return true
  }

  const checkMemberAge = (config = {}) => {
    const { displayRules = {} } = config
    const { showToAllAge = false } = displayRules
    const { birthdate } = member || {}

    if (showToAllAge) {
      return true
    }

    const { maxAgeOption, maximumAge } = displayRules
    if (maxAgeOption && maximumAge) {
      if (maxAgeOption === 'Registration Date') {
        const memberAge = calculateAge(birthdate)
        return maximumAge >= memberAge
      }
    }

    return true
  }

  const checkEligibleProducts = () => {
    const { products } = member || {}

    const eligibleProducts = products.reduce((acc, product) => {
      if (isProductOwnerSupportVoucher(product)) {
        acc.push(product)
      }
      return acc
    }, [])

    return eligibleProducts.length > 0
  }

  const voucherConfig = getVoucherConfigs(formData)
  const validProviders = []

  voucherConfig.forEach(config => {
    const hasCorrectFormType = checkFormType(config)
    const hasRequiredTypes = checkMemberTypes(config)
    const hasRequiredGrants = checkMemberGrants(config)
    const isCorrectAge = checkMemberAge(config)
    const hasEligibleProducts = checkEligibleProducts()

    const showVoucherConfig =
      hasCorrectFormType && hasRequiredTypes && hasRequiredGrants && isCorrectAge && hasEligibleProducts

    if (showVoucherConfig) {
      const { id, voucherProviderName, voucherIssuer } = config
      validProviders.push({
        id,
        issuer: voucherIssuer,
        name: voucherProviderName,
      })
    }
  })

  return {
    showVoucherInput: validProviders.length > 0,
    voucherProviders: [
      {
        key: 'voucherProviders',
        value: validProviders,
      },
    ],
  }
}
